import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/composites/PageHeader';
import { UnmatchedGroupsTable } from '@/components/composites/UnmatchedGroupsTable';
import { EmptyState } from '@/components/primitives/EmptyState';
import { Button } from '@/components/primitives/Button';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch, ApiError } from '@/lib/api';
import { formatDayShort, formatDurationMinutes, currentDate } from '@/lib/dates';
import { EMPLOYEES_WITH_STATS } from '@/data/employees';
import { REVIEW_ITEMS, REVIEW_ITEMS_TOTAL } from '@/data/employeeDetail';
import type { ReviewItem } from '@/data/employeeDetail';
import type { EmployeeFallbackSamplesResponse } from '@/types/dashboard';
import {
  ACTIVITY_REVIEW_TAB_LABEL,
  PRIVACY_DRAWER_TRIGGER_LABEL,
  UNDO_TOAST_MESSAGE_TEMPLATE,
  UNDO_TOAST_ACTION_LABEL,
  KILL_SWITCH_MODAL_CANCEL_BUTTON,
  SCAN_RANGE_QUEUED_MESSAGE,
  SCAN_RANGE_REPROCESS_NOISE_LABEL,
  SCAN_DISABLED_KILL_SWITCH_HINT,
} from '@/content/activity-copy';
import { PRIVACY_STATEMENT_TITLE, PRIVACY_STATEMENT_PARAGRAPHS } from '@/content/privacy-statement';
import styles from './EmployeeReviewPage.module.css';

/**
 * Task 188 — full-list drill-down for the "Needs Your Review" banner on
 * /employees/:employeeId. Shares its table with the detail page's top-N
 * summary via `UnmatchedGroupsTable`. Visibility (per-tenant + per-member
 * employee filter) is enforced server-side; the endpoint's existing 404
 * path handles "employee not visible" without leaking which it is.
 *
 * Phase 6-A (feature-200): Adds an Activities tab alongside the existing
 * "Needs Your Review" tab.
 */
export function EmployeeReviewPage() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const { mockupMode, currentOrg, dateRange } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  // Live mode: pull every unmatched group for the employee. The server
  // clamps `limit` to `DASHBOARD_MAX_LIMIT` (config.dashboard.maxLimit) —
  // pass MAX_SAFE_INTEGER so the clamp is the only ceiling and no new
  // tier-2 constant is introduced on the frontend (CONVENTIONS.md).
  const fallbackQuery = useQuery({
    queryKey: ['employeeFallbackSamplesAll', orgId, employeeId, dateRange],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams();
      qs.set('limit', String(Number.MAX_SAFE_INTEGER));
      if (dateRange.start) qs.set('startDate', dateRange.start);
      if (dateRange.end) qs.set('endDate', dateRange.end);
      return apiFetch<EmployeeFallbackSamplesResponse>(
        `/api/v1/orgs/${orgId}/employees/${employeeId}/events/fallback-samples?${qs.toString()}`,
        token,
      );
    },
    enabled: !mockupMode && !!orgId && !!employeeId,
  });

  // Live mode also needs the employee's display name.
  const employeeQuery = useQuery({
    queryKey: ['employeesRoster', orgId],
    queryFn: async () => {
      const token = await getToken();
      return apiFetch<{ employees: Array<{ id: string; name: string }> }>(
        `/api/v1/orgs/${orgId}/employees`,
        token,
      );
    },
    enabled: !mockupMode && !!orgId,
  });

  if (mockupMode) {
    const employee = EMPLOYEES_WITH_STATS.find((e) => e.id === employeeId);
    if (!employee) {
      return (
        <>
          <PageHeader title="Employee not found" />
          <EmptyState
            title="Employee not found"
            description="This employee does not exist in the current organization."
          />
        </>
      );
    }
    return (
      <ReviewPageContent
        employeeId={employee.id}
        employeeName={employee.name}
        items={REVIEW_ITEMS}
        totalGroups={REVIEW_ITEMS_TOTAL}
        orgId={orgId}
      />
    );
  }

  if (fallbackQuery.isLoading || employeeQuery.isLoading) {
    return (
      <>
        <PageHeader title="Loading…" />
        <EmptyState title="Loading…" description="" />
      </>
    );
  }

  const liveEmployee = employeeQuery.data?.employees.find((e) => e.id === employeeId);
  if (fallbackQuery.isError || !liveEmployee) {
    return (
      <>
        <PageHeader title="Employee not found" />
        <EmptyState
          title="Employee not found"
          description="This employee does not exist in the current organization."
        />
      </>
    );
  }

  const samples = fallbackQuery.data?.samples ?? [];
  const totalGroups = fallbackQuery.data?.totalGroups ?? 0;
  const items: ReviewItem[] = samples.map((s) => {
    const first = formatDayShort(s.firstOccurredAt);
    const last = formatDayShort(s.lastOccurredAt);
    return {
      app: s.app,
      title: s.title,
      screenshots: s.eventCount,
      sessions: s.sessions,
      time: `${s.totalHours.toFixed(1)}h`,
      lastSeen: last,
      dateRange: first === last ? first : `${first} – ${last}`,
    };
  });

  return (
    <ReviewPageContent
      employeeId={liveEmployee.id}
      employeeName={liveEmployee.name}
      items={items}
      totalGroups={totalGroups}
      orgId={orgId}
    />
  );
}

// ---------------------------------------------------------------------------
// Privacy drawer — keyboard-dismissible, focus-trapped dialog
// ---------------------------------------------------------------------------

function PrivacyDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const panelRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div
      className={styles.privacyOverlay}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={PRIVACY_STATEMENT_TITLE}
        tabIndex={-1}
        className={styles.privacyDialog}
      >
        <div className={styles.privacyDialogHeader}>
          <h2 className={styles.privacyDialogTitle}>{PRIVACY_STATEMENT_TITLE}</h2>
          <button
            type="button"
            className={styles.privacyDialogClose}
            onClick={onClose}
            aria-label="Close panel"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
              <path
                d="M15 5L5 15M5 5l10 10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className={styles.privacyDialogBody}>
          {PRIVACY_STATEMENT_PARAGRAPHS.map((p, i) => (
            <p key={i} className={styles.privacyParagraph}>
              {p}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Undo toast
// ---------------------------------------------------------------------------

interface UndoToast {
  activityId: string;
  activityName: string;
  expiresAt: number; // timestamp ms
}

function UndoToastBar({
  toast,
  onUndo,
  onDismiss,
}: {
  toast: UndoToast;
  onUndo: (activityId: string) => void;
  onDismiss: () => void;
}) {
  // `remaining` starts as null; the first useEffect tick fills it in so we
  // never call Date.now() during the initial render (banned by react-hooks/purity).
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    function tick() {
      const now = currentDate().getTime();
      const left = Math.max(0, toast.expiresAt - now);
      setRemaining(left);
      if (now >= toast.expiresAt) onDismiss();
    }
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [toast.expiresAt, onDismiss]);

  const safeRemaining = remaining ?? 0;
  const mm = String(Math.floor(safeRemaining / 60000)).padStart(2, '0');
  const ss = String(Math.floor((safeRemaining % 60000) / 1000)).padStart(2, '0');
  const message = UNDO_TOAST_MESSAGE_TEMPLATE.replace('{name}', toast.activityName);

  return (
    <div className={styles.undoToast} role="status" aria-live="polite">
      <span>{message}</span>
      <button
        type="button"
        className={styles.undoToastAction}
        onClick={() => onUndo(toast.activityId)}
      >
        {UNDO_TOAST_ACTION_LABEL} ({mm}:{ss})
      </button>
      <button
        type="button"
        className={styles.undoToastDismiss}
        aria-label="Dismiss notification"
        onClick={onDismiss}
      >
        ×
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scan date range modal
// ---------------------------------------------------------------------------

function ScanRangeModal({
  open,
  onClose,
  onQueued,
  orgId,
  employeeId,
  disabled,
  defaultFrom,
  defaultTo,
}: {
  open: boolean;
  onClose: () => void;
  onQueued: () => void;
  orgId: string;
  employeeId: string;
  disabled: boolean;
  defaultFrom: string;
  defaultTo: string;
}) {
  const getToken = useToken();
  const { mockupMode } = useAppContext();

  // Date values default to the computed range passed from the parent;
  // the parent computes them inside an event handler (not during render).
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [reprocessNoise, setReprocessNoise] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mockupMode) {
      onQueued();
      onClose();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const token = await getToken();
      await apiFetch(`/api/v1/orgs/${orgId}/activities/scan-range`, token, {
        method: 'POST',
        body: JSON.stringify({ employeeId, from, to, reprocessNoise }),
      });
      onQueued();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to queue scan');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className={styles.privacyOverlay}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Scan date range"
        className={styles.scanModal}
      >
        <div className={styles.privacyDialogHeader}>
          <h2 className={styles.privacyDialogTitle}>Scan date range</h2>
          <button
            type="button"
            className={styles.privacyDialogClose}
            onClick={onClose}
            aria-label="Close panel"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
              <path
                d="M15 5L5 15M5 5l10 10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <form onSubmit={(e) => void handleSubmit(e)} className={styles.scanForm}>
          <label className={styles.scanLabel}>
            From
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.currentTarget.value)}
              className={styles.scanDateInput}
            />
          </label>
          <label className={styles.scanLabel}>
            To
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.currentTarget.value)}
              className={styles.scanDateInput}
            />
          </label>
          <label className={styles.scanCheckboxLabel}>
            <input
              type="checkbox"
              checked={reprocessNoise}
              onChange={(e) => setReprocessNoise(e.currentTarget.checked)}
            />
            {SCAN_RANGE_REPROCESS_NOISE_LABEL}
          </label>
          {error && <p className={styles.scanError}>{error}</p>}
          <div className={styles.scanActions}>
            <Button variant="secondary" type="button" onClick={onClose}>
              {KILL_SWITCH_MODAL_CANCEL_BUTTON}
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={submitting}
              disabled={disabled}
              title={disabled ? SCAN_DISABLED_KILL_SWITCH_HINT : undefined}
            >
              Scan
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activities tab
// ---------------------------------------------------------------------------

interface Activity {
  id: string;
  name: string;
  status: 'proposed' | 'confirmed' | 'dismissed';
  eventCount: number;
  insightfulMinutes: number;
  firstSeenAt: string;
  lastSeenAt: string;
}

interface BreakdownGroup {
  app: string;
  title: string | null;
  url: string | null;
  eventCount: number;
}

function ActivityBreakdownPanel({ activityId, orgId }: { activityId: string; orgId: string }) {
  const getToken = useToken();
  const { data, isLoading } = useQuery({
    queryKey: ['activity-breakdown', orgId, activityId],
    queryFn: async () => {
      const token = await getToken();
      return apiFetch<{ groups: BreakdownGroup[] }>(
        `/api/v1/orgs/${orgId}/activities/${activityId}/events-breakdown`,
        token,
      );
    },
  });

  if (isLoading) {
    return <div className={styles.activityBreakdownPanel}>Loading…</div>;
  }
  const groups = data?.groups ?? [];
  if (groups.length === 0) {
    return (
      <div className={styles.activityBreakdownPanel}>
        <p className={styles.activityEmpty}>No events found.</p>
      </div>
    );
  }
  return (
    <div className={styles.activityBreakdownPanel}>
      <table className={styles.activityBreakdownTable}>
        <thead>
          <tr>
            <th className={styles.activityBreakdownTh}>App</th>
            <th className={styles.activityBreakdownTh}>Window / Page</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g, i) => (
            <tr key={i}>
              <td className={styles.activityBreakdownTd}>{g.app}</td>
              <td className={styles.activityBreakdownTd}>
                <span>{g.title ?? '—'}</span>
                {g.url && <span className={styles.activityBreakdownUrl}>{g.url}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActivitiesTab({ orgId, employeeId }: { orgId: string; employeeId: string }) {
  const { mockupMode, dateRange } = useAppContext();
  const getToken = useToken();
  const queryClient = useQueryClient();
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanDefaultFrom, setScanDefaultFrom] = useState('');
  const [scanDefaultTo, setScanDefaultTo] = useState('');
  const [undoToast, setUndoToast] = useState<UndoToast | null>(null);
  const [scanToast, setScanToast] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  function toggleExpand(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleOpenScan() {
    // Compute default date range inside an event handler — never during render.
    const now = currentDate();
    setScanDefaultTo(now.toISOString().slice(0, 10));
    now.setDate(now.getDate() - 30);
    setScanDefaultFrom(now.toISOString().slice(0, 10));
    setScanOpen(true);
  }

  // Proposed activities
  const proposedQuery = useQuery({
    queryKey: ['activities', orgId, employeeId, 'proposed', dateRange.start, dateRange.end],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams({ employeeId: employeeId, status: 'proposed' });
      if (dateRange.start) qs.set('from', dateRange.start);
      if (dateRange.end) qs.set('to', dateRange.end);
      return apiFetch<{ activities: Activity[] }>(
        `/api/v1/orgs/${orgId}/activities?${qs.toString()}`,
        token,
      );
    },
    enabled: !mockupMode && !!orgId && !!employeeId,
  });

  // Confirmed activities
  const confirmedQuery = useQuery({
    queryKey: ['activities', orgId, employeeId, 'confirmed', dateRange.start, dateRange.end],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams({ employeeId: employeeId, status: 'confirmed' });
      if (dateRange.start) qs.set('from', dateRange.start);
      if (dateRange.end) qs.set('to', dateRange.end);
      return apiFetch<{ activities: Activity[] }>(
        `/api/v1/orgs/${orgId}/activities?${qs.toString()}`,
        token,
      );
    },
    enabled: !mockupMode && !!orgId && !!employeeId,
  });

  // Confirm mutation
  const confirmMutation = useMutation({
    mutationFn: async (activityId: string) => {
      const token = await getToken();
      return apiFetch<{ undoExpiresAt: string }>(
        `/api/v1/orgs/${orgId}/activities/${activityId}/confirm`,
        token,
        { method: 'POST' },
      );
    },
    onSuccess: (data, activityId) => {
      const activity = proposedQuery.data?.activities.find((a) => a.id === activityId);
      if (activity) {
        setUndoToast({
          activityId,
          activityName: activity.name,
          expiresAt: data.undoExpiresAt
            ? Date.parse(data.undoExpiresAt)
            : currentDate().getTime() + 5 * 60 * 1000,
        });
      }
      void queryClient.invalidateQueries({ queryKey: ['activities', orgId, employeeId] });
    },
  });

  // Undo confirm mutation
  const undoConfirmMutation = useMutation({
    mutationFn: async (activityId: string) => {
      const token = await getToken();
      return apiFetch(`/api/v1/orgs/${orgId}/activities/${activityId}/undo-confirm`, token, {
        method: 'POST',
      });
    },
    onSuccess: () => {
      setUndoToast(null);
      void queryClient.invalidateQueries({ queryKey: ['activities', orgId, employeeId] });
    },
  });

  // Dismiss mutation
  const dismissMutation = useMutation({
    mutationFn: async (activityId: string) => {
      const token = await getToken();
      return apiFetch(`/api/v1/orgs/${orgId}/activities/${activityId}/dismiss`, token, {
        method: 'POST',
      });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        // 409 conflict — confirmation required; future: open post-window modal
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['activities', orgId, employeeId] });
    },
  });

  const proposedActivities: Activity[] = mockupMode ? [] : (proposedQuery.data?.activities ?? []);
  const confirmedActivities: Activity[] = mockupMode ? [] : (confirmedQuery.data?.activities ?? []);

  return (
    <div className={styles.activitiesTab}>
      {/* Header row: scan button on left, privacy link on right */}
      <div className={styles.activitiesHeader}>
        <Button variant="secondary" size="sm" onClick={handleOpenScan}>
          Scan date range
        </Button>
        <button
          type="button"
          aria-label="Open privacy statement"
          className={styles.privacyTrigger}
          onClick={() => setPrivacyOpen(true)}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
            className={styles.privacyIcon}
          >
            <path
              d="M10 2L3 5v5c0 4.25 2.95 8.22 7 9.5 4.05-1.28 7-5.25 7-9.5V5l-7-3z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
          </svg>
          {PRIVACY_DRAWER_TRIGGER_LABEL}
        </button>
      </div>

      {/* Undo toast */}
      {undoToast && (
        <UndoToastBar
          toast={undoToast}
          onUndo={(id) => undoConfirmMutation.mutate(id)}
          onDismiss={() => setUndoToast(null)}
        />
      )}

      {/* Scan queued toast */}
      {scanToast && (
        <div className={styles.scanToast} role="status" aria-live="polite">
          {SCAN_RANGE_QUEUED_MESSAGE}
          <button
            type="button"
            className={styles.undoToastDismiss}
            aria-label="Dismiss"
            onClick={() => setScanToast(false)}
          >
            ×
          </button>
        </div>
      )}

      {/* Proposed section */}
      <section className={styles.activitySection}>
        <h3 className={styles.activitySectionTitle}>Proposed</h3>
        {proposedActivities.length === 0 ? (
          <p className={styles.activityEmpty}>
            {!mockupMode && proposedQuery.isLoading ? 'Loading…' : 'No proposed activities.'}
          </p>
        ) : (
          <ul className={styles.activityList}>
            {proposedActivities.map((activity) => {
              const isExpanded = expandedIds.has(activity.id);
              return (
                <li key={activity.id} className={styles.activityCard}>
                  <div className={styles.activityCardRow}>
                    <div className={styles.activityCardContent}>
                      <span className={styles.activityName}>{activity.name}</span>
                      <div className={styles.activityMetaRow}>
                        <span className={styles.activityMeta}>
                          {formatDurationMinutes(activity.insightfulMinutes)} ·{' '}
                          {formatDayShort(activity.firstSeenAt)} –{' '}
                          {formatDayShort(activity.lastSeenAt)}
                        </span>
                        <button
                          type="button"
                          className={styles.activityExpandBtn}
                          onClick={() => toggleExpand(activity.id)}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? 'Hide' : 'Review'}
                        </button>
                      </div>
                    </div>
                    <div className={styles.activityCardActions}>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => confirmMutation.mutate(activity.id)}
                        loading={
                          confirmMutation.isPending && confirmMutation.variables === activity.id
                        }
                      >
                        Confirm
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => dismissMutation.mutate(activity.id)}
                        loading={
                          dismissMutation.isPending && dismissMutation.variables === activity.id
                        }
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>
                  {isExpanded && <ActivityBreakdownPanel activityId={activity.id} orgId={orgId} />}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Confirmed section */}
      <section className={styles.activitySection}>
        <h3 className={styles.activitySectionTitle}>Confirmed</h3>
        {confirmedActivities.length === 0 ? (
          <p className={styles.activityEmpty}>
            {!mockupMode && confirmedQuery.isLoading ? 'Loading…' : 'No confirmed activities.'}
          </p>
        ) : (
          <ul className={styles.activityList}>
            {confirmedActivities.map((activity) => (
              <li key={activity.id} className={styles.activityCard}>
                <div className={styles.activityCardRow}>
                  <div className={styles.activityCardContent}>
                    <span className={styles.activityName}>{activity.name}</span>
                    <span className={styles.activityMeta}>
                      {formatDurationMinutes(activity.insightfulMinutes)} ·{' '}
                      {formatDayShort(activity.firstSeenAt)} – {formatDayShort(activity.lastSeenAt)}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Loose events section */}
      <section className={styles.activitySection}>
        <h3 className={styles.activitySectionTitle}>Loose events</h3>
        <p className={styles.activityEmpty}>
          Events not yet attached to any activity appear in the &ldquo;Needs Your Review&rdquo; tab.
        </p>
      </section>

      {/* Privacy drawer */}
      <PrivacyDrawer open={privacyOpen} onClose={() => setPrivacyOpen(false)} />

      {/* Scan range modal */}
      <ScanRangeModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onQueued={() => setScanToast(true)}
        orgId={orgId}
        employeeId={employeeId}
        disabled={false}
        defaultFrom={scanDefaultFrom}
        defaultTo={scanDefaultTo}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReviewPageContent — tabbed surface
// ---------------------------------------------------------------------------

function ReviewPageContent({
  employeeId,
  employeeName,
  items,
  totalGroups,
  orgId,
}: {
  employeeId: string;
  employeeName: string;
  items: readonly ReviewItem[];
  totalGroups: number;
  orgId: string;
}) {
  const [activeTab, setActiveTab] = useState<'review' | 'activities'>('review');

  return (
    <>
      <PageHeader title={`${employeeName} — Unmatched activity`} />
      <div className={styles.backLink}>
        <Link to={`/employees/${employeeId}`}>&larr; Back to {employeeName}</Link>
      </div>

      {/* Tab bar */}
      <div role="tablist" className={styles.tabList} aria-label="Review tabs">
        <button
          role="tab"
          type="button"
          aria-selected={activeTab === 'review'}
          aria-controls="tab-panel-review"
          id="tab-review"
          className={`${styles.tab} ${activeTab === 'review' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('review')}
        >
          Needs Your Review
        </button>
        <button
          role="tab"
          type="button"
          aria-selected={activeTab === 'activities'}
          aria-controls="tab-panel-activities"
          id="tab-activities"
          className={`${styles.tab} ${activeTab === 'activities' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('activities')}
        >
          {ACTIVITY_REVIEW_TAB_LABEL}
        </button>
      </div>

      {/* "Needs Your Review" panel */}
      <div
        role="tabpanel"
        id="tab-panel-review"
        aria-labelledby="tab-review"
        hidden={activeTab !== 'review'}
      >
        {totalGroups === 0 ? (
          <EmptyState
            title="No unmatched activity"
            description="Every event in this date range has a matching rule."
          />
        ) : (
          <UnmatchedGroupsTable
            items={items}
            mode="all"
            totalGroups={totalGroups}
            employeeId={employeeId}
          />
        )}
      </div>

      {/* Activities panel */}
      <div
        role="tabpanel"
        id="tab-panel-activities"
        aria-labelledby="tab-activities"
        hidden={activeTab !== 'activities'}
      >
        <ActivitiesTab orgId={orgId} employeeId={employeeId} />
      </div>
    </>
  );
}
